import { useMemo } from 'react'
import { useSelector } from '@xstate/react'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCartMachineActor } from 'machines/cart/getCartMachineActor'
import { selectItemsInActiveVisibleCart } from 'machines/cart/selectors'
import { getAllOffers } from 'selectors/offerSelectors'
import { isExperienceItem, isTransferItem, isLEHotelItem, isBedbankItem, isLuxPlusSubscriptionItem } from 'lib/checkout/checkoutUtils'
import { CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE } from 'constants/checkout'
import { shouldWaiveLuxPlusJoiningFee } from 'lib/userCart/multiCartSnapshotUtils'

export function useNonDeletedUserCartItemsArray(): Array<App.Checkout.AnyItem> {
  const multiCartActorRef = getCartMachineActor() ?? undefined
  return useSelector(multiCartActorRef, (snapshot) => {
    if (!snapshot) return []
    const allItems = selectItemsInActiveVisibleCart(snapshot)
    return allItems
      .filter(multiCartItem => multiCartItem.state !== 'deleted')
      .map(multiCartItem => multiCartItem.item)
  }) ?? []
}

export function useActiveUserCartItems(): Array<App.Checkout.AnyItem> {
  const multiCartActorRef = getCartMachineActor() ?? undefined
  return useSelector(multiCartActorRef, (snapshot) => {
    if (!snapshot) return []
    const allItems = selectItemsInActiveVisibleCart(snapshot)
    return allItems
      .filter(multiCartItem =>
        multiCartItem.state === 'active' &&
        multiCartItem.item.itemType !== CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE,
      )
      .map(multiCartItem => multiCartItem.item)
  }) ?? []
}

export function useIsLuxPlusSubscriptionUserCartItemActive(): boolean {
  const multiCartActorRef = getCartMachineActor() ?? undefined
  return useSelector(multiCartActorRef, (snapshot) => {
    if (!snapshot) return false
    const allItems = selectItemsInActiveVisibleCart(snapshot)
    const subscriptionItem = allItems.find(
      multiCartItem =>
        isLuxPlusSubscriptionItem(multiCartItem.item) &&
        multiCartItem.state === 'active',
    )
    return !!subscriptionItem
  }) ?? false
}

export function useSelectCartOffers(): Record<string, App.AnyOffer> {
  const nonDeletedItems = useNonDeletedUserCartItemsArray()
  const offers = useAppSelector(getAllOffers)

  return useMemo(() => {
    return nonDeletedItems.reduce((acc: Record<string, App.AnyOffer>, item: App.Checkout.AnyItem) => {
      if ((isExperienceItem(item) || isTransferItem(item)) && offers?.[item?.experienceId]) {
        acc[item.experienceId] = offers[item.experienceId]
      } else if ((isLEHotelItem(item) || isBedbankItem(item)) && offers?.[item?.offerId]) {
        acc[item.offerId] = offers[item.offerId] as App.Offer
      }
      return acc
    }, {})
  }, [nonDeletedItems, offers])
}

export function useShouldWaiveLuxPlusJoiningFee(): boolean {
  const activeItems = useActiveUserCartItems()
  const cartOffers = useSelectCartOffers()

  return useMemo(() => {
    return shouldWaiveLuxPlusJoiningFee(activeItems, cartOffers)
  }, [activeItems, cartOffers])
}
